import { ArrowLeft, ChevronDown, LayoutDashboard, LogOut, Menu, Moon, Rocket, Shield, Sun, UserCircle2, X, Compass } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'

import Button from './Button'
import CompassMark from './CompassMark'
import SearchInput from './SearchInput'
import useClickOutside from '../../hooks/useClickOutside'
import { useCurrency } from '../../context/CurrencyContext'

const MotionDiv = motion.div

const STORAGE_KEY = 'ai-compass-theme'
const ADMIN_EMAILS = ['singhmedhansh07@gmail.com']
const dropdownTransition = { duration: 0.2, ease: [0.22, 1, 0.36, 1] }

function getInitialTheme() {
  if (typeof window === 'undefined') {
    return false
  }

  const storedTheme = window.localStorage.getItem(STORAGE_KEY)

  if (storedTheme) {
    return storedTheme === 'dark'
  }

  return false
}

function Navbar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedCurrency, setSelectedCurrency, currencies } = useCurrency()
  // /tools has its own page-level search; suppress the navbar one to avoid duplication
  const hideSearchOnRoute = location.pathname === '/tools'
  const [searchValue, setSearchValue] = useState('')
  const [isDark, setIsDark] = useState(getInitialTheme)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isGuidesMenuOpen, setIsGuidesMenuOpen] = useState(false)
  const [isStudentHubMenuOpen, setIsStudentHubMenuOpen] = useState(false)
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false)
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false)
  const [failedAvatarUrl, setFailedAvatarUrl] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authRefreshKey, setAuthRefreshKey] = useState(0)
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')
    } catch {
      return null
    }
  })
  const [showProfileTooltip, setShowProfileTooltip] = useState(() => {
    try {
      const userObj = localStorage.getItem('user')
      const dismissed = localStorage.getItem('ai-compass-profile-tooltip-dismissed') === 'true'
      return !!userObj && !dismissed
    } catch {
      return false
    }
  })
  const avatarFailed = failedAvatarUrl != null && failedAvatarUrl === user?.picture
  const [scrolled, setScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isCurrencyMenuOpen, setIsCurrencyMenuOpen] = useState(false)
  const menuRef = useClickOutside(() => setIsProfileMenuOpen(false))
  const guidesMenuRef = useClickOutside(() => setIsGuidesMenuOpen(false))
  const studentHubMenuRef = useClickOutside(() => setIsStudentHubMenuOpen(false))
  const currencyMenuRef = useClickOutside(() => setIsCurrencyMenuOpen(false))
  const toolsMenuRef = useClickOutside(() => setIsToolsMenuOpen(false))
  const headerRef = useClickOutside(() => setIsMobileMenuOpen(false))
  const isAdmin = Boolean(user && (user.is_admin || ADMIN_EMAILS.includes(user.email)))
  // "Founder account" = owns at least one Submission (see is_founder in
  // app/api_routes.py's _serialize_user, computed from founder_user_id —
  // no separate flag to keep in sync).
  const isFounder = Boolean(user && user.is_founder)
  const avatarLetter = useMemo(
    () => String(user?.name || user?.email || 'U').charAt(0).toUpperCase(),
    [user?.email, user?.name],
  )

  const syncThemeFromStorage = () => {
    if (typeof window === 'undefined') {
      return
    }

    const storedTheme = window.localStorage.getItem(STORAGE_KEY)
    setIsDark(storedTheme === 'dark')
  }

  useEffect(() => {
    if (isDark) {
      document.documentElement.setAttribute('data-theme', 'dark')
    } else {
      document.documentElement.removeAttribute('data-theme')
    }
    window.localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light')
  }, [isDark])

  useEffect(() => {
    const handleThemeChange = () => syncThemeFromStorage()

    window.addEventListener('storage', handleThemeChange)
    window.addEventListener('themeChanged', handleThemeChange)

    return () => {
      window.removeEventListener('storage', handleThemeChange)
      window.removeEventListener('themeChanged', handleThemeChange)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    handleScroll() // initialize on mount in case page loads scrolled
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (!isMobileMenuOpen) return
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false)
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isMobileMenuOpen])

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined
    // Threshold is relative to where the page was when the menu opened, so a
    // page that's already scrolled doesn't close the menu the instant it opens.
    const openedAtY = window.scrollY
    const handleScrollClose = () => {
      if (Math.abs(window.scrollY - openedAtY) > 40) setIsMobileMenuOpen(false)
    }
    window.addEventListener('scroll', handleScrollClose, { passive: true })
    return () => window.removeEventListener('scroll', handleScrollClose)
  }, [isMobileMenuOpen])

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || 'null')
        setUser(storedUser)
        setIsAuthenticated(Boolean(storedUser))
        setAuthRefreshKey((value) => value + 1)
        const dismissed = localStorage.getItem('ai-compass-profile-tooltip-dismissed') === 'true'
        setShowProfileTooltip(Boolean(storedUser) && !dismissed)
      } catch {
        setUser(null)
        setIsAuthenticated(false)
        setShowProfileTooltip(false)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('userLoggedIn', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('userLoggedIn', handleStorageChange)
    }
  }, [])

  useEffect(() => {
    let active = true

    const verifyAuthState = async () => {
      let storedUser = null

      try {
        storedUser = JSON.parse(localStorage.getItem('user') || 'null')
      } catch {
        storedUser = null
      }

      if (!storedUser) {
        if (active) {
          setUser(null)
          setIsAuthenticated(false)
        }
        return
      }

      try {
        const response = await fetch('/api/v1/auth/me', { credentials: 'include' })

        if (!response.ok) {
          localStorage.removeItem('user')
          if (window.posthog) {
            window.posthog.reset();
          }
          if (active) {
            setUser(null)
            setIsAuthenticated(false)
          }
          return
        }

        const fullUser = await response.json()
        const mergedUser = {
          ...storedUser,
          ...fullUser,
        }
        localStorage.setItem('user', JSON.stringify(mergedUser))
        if (window.posthog && mergedUser) {
          window.posthog.identify(mergedUser.id, {
            is_verified: mergedUser.is_verified
          });
        }

        if (active) {
          setUser(mergedUser)
          setIsAuthenticated(true)
        }

        // Client-side convenience only — the actual boundary is
        // enforce_password_change_gate() in app/__init__.py, which rejects
        // the underlying API calls regardless of what page is showing.
        // This just gets a stray authenticated tab (e.g. opened from an
        // old bookmark) to the right place instead of a wall of 403s.
        if (
          mergedUser.must_change_password
          && location.pathname !== '/account/change-password'
          && location.pathname !== '/login'
        ) {
          navigate('/account/change-password', { replace: true })
        }
      } catch {
        if (active) {
          setUser(storedUser)
          setIsAuthenticated(Boolean(storedUser))
        }
        if (window.posthog && storedUser) {
          window.posthog.identify(storedUser.id, {
            is_verified: storedUser.is_verified
          });
        }
      }
    }

    verifyAuthState()

    return () => {
      active = false
    }
  }, [authRefreshKey])

  const toggleDarkMode = () => {
    const nextMode = !isDark

    setIsDark(nextMode)
  }

  const handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return
    }

    const query = searchValue.trim()
    if (!query) {
      navigate('/tools')
      return
    }

    navigate(`/tools?q=${encodeURIComponent(query)}`)
  }

  const handleLogout = async () => {
    // Clear the server session + Flask-Login remember cookie so the user
    // stays logged out (otherwise the remember cookie would re-auth them).
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // best-effort — still clear client state below
    }
    localStorage.removeItem('user')
    if (window.posthog) {
      window.posthog.reset();
    }
    setUser(null)
    setIsAuthenticated(false)
    setShowProfileTooltip(false)
    window.dispatchEvent(new Event('userLoggedIn'))
    setIsProfileMenuOpen(false)
    navigate('/')
  }

  return (
    <header
      ref={headerRef}
      className={`sticky z-50 transition-all duration-500 ease-out ${
        scrolled
          ? 'top-0 md:top-4 md:px-6 bg-bg/95 md:bg-transparent backdrop-blur-md md:backdrop-blur-none border-b border-line md:border-none shadow-sm md:shadow-none'
          : 'top-0 px-0 bg-transparent border-b border-transparent'
      }`}
    >
      <div
        className={`mx-auto flex w-full flex-wrap items-center gap-3 transition-all duration-500 ease-out ${
          scrolled
            ? 'max-w-7xl md:max-w-5xl md:rounded-full md:border md:border-line md:bg-bg/95 px-4 sm:px-6 md:px-6 py-2 md:shadow-lg md:backdrop-blur-xl'
            : 'max-w-7xl bg-bg/95 sm:bg-transparent border-b border-transparent px-4 sm:px-6 md:px-8 py-3'
        }`}
      >
        {location.pathname !== '/' && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="group flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line bg-bg-elev/60 backdrop-blur-lg text-ink-2 shadow-sm transition-all duration-300 hover:border-accent hover:text-accent hover:scale-105 active:scale-95"
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
          </button>
        )}

        <Link
          to="/"
          className="flex shrink-0 items-center gap-2 text-lg font-bold tracking-tight text-ink transition-colors hover:text-accent-ink"
        >
          <CompassMark size={28} />
          AI Compass
        </Link>

        {hideSearchOnRoute ? null : (
          <div className="order-3 w-full sm:order-2 sm:flex-1 sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl">
            <SearchInput
              value={searchValue}
              onChange={setSearchValue}
              onClear={() => setSearchValue('')}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search or describe what you need..."
            />
          </div>
        )}

        <nav aria-label="Primary" className="order-2 ml-auto hidden items-center gap-2 sm:order-3 lg:flex">
          <div
            className="relative"
            ref={toolsMenuRef}
            onMouseEnter={() => setIsToolsMenuOpen(true)}
            onMouseLeave={() => setIsToolsMenuOpen(false)}
          >
            <Button variant="ghost" size="sm" className="text-ink-2">
              Tools
              <ChevronDown className="h-4 w-4 ml-1 transition-transform" style={{ transform: isToolsMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </Button>

            <AnimatePresence initial={false}>
              {isToolsMenuOpen && (
                <MotionDiv
                  key="tools-menu"
                  role="menu"
                  aria-label="Tools menu"
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={dropdownTransition}
                  className="absolute left-0 mt-1 w-56 origin-top-left overflow-hidden rounded-xl border border-line bg-bg-elev shadow-lg z-50"
                >
                  <Link
                    to="/tools"
                    onClick={() => setIsToolsMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                    role="menuitem"
                  >
                    Catalog
                  </Link>
                  <Link
                    to="/collections"
                    onClick={() => setIsToolsMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                    role="menuitem"
                  >
                    Collections
                  </Link>
                </MotionDiv>
              )}
            </AnimatePresence>
          </div>

          <Link to="/community">
            <Button variant="ghost" size="sm" className="text-ink-2">
              Community
            </Button>
          </Link>

          <Link to="/model-comparison">
            <Button variant="ghost" size="sm" className="text-ink-2">
              LLM Cost Calculator
            </Button>
          </Link>

          <div
            className="relative"
            ref={studentHubMenuRef}
            onMouseEnter={() => setIsStudentHubMenuOpen(true)}
            onMouseLeave={() => setIsStudentHubMenuOpen(false)}
          >
            <Button variant="ghost" size="sm" className="text-ink-2">
              Student Hub
              <ChevronDown className="h-4 w-4 ml-1 transition-transform" style={{ transform: isStudentHubMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </Button>

            <AnimatePresence initial={false}>
              {isStudentHubMenuOpen && (
                <MotionDiv
                  key="student-hub-menu"
                  role="menu"
                  aria-label="Student Hub menu"
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={dropdownTransition}
                  className="absolute left-0 mt-1 w-56 origin-top-left overflow-hidden rounded-xl border border-line bg-bg-elev shadow-lg"
                >
                  <Link
                    to="/syllabus-parser"
                    onClick={() => setIsStudentHubMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                    role="menuitem"
                  >
                    Syllabus Parser
                  </Link>
                  <Link
                    to="/student-discounts"
                    onClick={() => setIsStudentHubMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                    role="menuitem"
                  >
                    Student Discounts
                  </Link>
                  <Link
                    to="/stacks"
                    onClick={() => setIsStudentHubMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                    role="menuitem"
                  >
                    Community Stacks
                  </Link>

                </MotionDiv>
              )}
            </AnimatePresence>
          </div>

          <div
            className="relative"
            ref={guidesMenuRef}
            onMouseEnter={() => setIsGuidesMenuOpen(true)}
            onMouseLeave={() => setIsGuidesMenuOpen(false)}
          >
            <Button variant="ghost" size="sm" className="text-ink-2">
              Guides
              <ChevronDown className="h-4 w-4 ml-1 transition-transform" style={{ transform: isGuidesMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </Button>

            <AnimatePresence initial={false}>
              {isGuidesMenuOpen ? (
              <MotionDiv
                key="guides-menu"
                role="menu"
                aria-label="Guides menu"
                initial={{ opacity: 0, y: -6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={dropdownTransition}
                className="absolute left-0 mt-1 w-56 origin-top-left overflow-hidden rounded-xl border border-line bg-bg-elev shadow-lg"
              >
                <Link
                  to="/best-ai-tools-for-students"
                  onClick={() => setIsGuidesMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                  role="menuitem"
                >
                  Best AI Tools for Students
                </Link>
                <Link
                  to="/best-ai-tools-for-teachers"
                  onClick={() => setIsGuidesMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                  role="menuitem"
                >
                  Best AI Tools for Teachers
                </Link>
                <Link
                  to="/best-free-ai-tools"
                  onClick={() => setIsGuidesMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                  role="menuitem"
                >
                  Best Free AI Tools
                </Link>
                <Link
                  to="/best-coding-tools-for-students"
                  onClick={() => setIsGuidesMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                  role="menuitem"
                >
                  Best Coding Tools for Students
                </Link>
                <div className="border-t border-line my-1"></div>
                <div className="px-4 py-1.5 text-xs font-bold tracking-wider text-muted uppercase">How-To Guides</div>
                <Link
                  to="/guides/github-student-pack"
                  onClick={() => setIsGuidesMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                  role="menuitem"
                >
                  Claim GitHub Student Pack
                </Link>
                <Link
                  to="/guides/notion-student-premium"
                  onClick={() => setIsGuidesMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                  role="menuitem"
                >
                  Claim Notion Education Plan
                </Link>
                <Link
                  to="/guides/jetbrains-student-license"
                  onClick={() => setIsGuidesMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                  role="menuitem"
                >
                  Claim JetBrains License
                </Link>
              </MotionDiv>
              ) : null}
            </AnimatePresence>
          </div>

          {isAdmin ? (
            <Link to="/admin" className="px-1 text-xs font-medium text-muted hover:text-ink-2">
              Admin
            </Link>
          ) : null}

          {/* Currency Dropdown (Desktop) */}
          <div className="relative" ref={currencyMenuRef}>
            <button
              type="button"
              onClick={() => setIsCurrencyMenuOpen((prev) => !prev)}
              className="flex items-center gap-1.5 h-10 rounded-lg border border-line-strong bg-bg-elev/60 backdrop-blur-lg px-3 text-xs font-semibold text-ink-2 transition-colors hover:bg-bg-sunk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent select-none"
              aria-haspopup="listbox"
              aria-expanded={isCurrencyMenuOpen}
            >
              <span>{selectedCurrency} ({currencies.find(c => c.code === selectedCurrency)?.symbol})</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted transition-transform duration-200" style={{ transform: isCurrencyMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>

            <AnimatePresence initial={false}>
              {isCurrencyMenuOpen && (
                <MotionDiv
                  key="currency-menu"
                  role="listbox"
                  aria-label="Currency selection"
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={dropdownTransition}
                  className="absolute right-0 mt-2 w-40 origin-top-right overflow-hidden rounded-xl border border-line bg-bg-elev p-1 shadow-lg z-50"
                >
                  {currencies.map((curr) => (
                    <button
                      key={curr.code}
                      type="button"
                      onClick={() => {
                        setSelectedCurrency(curr.code)
                        setIsCurrencyMenuOpen(false)
                      }}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs font-medium transition ${
                        selectedCurrency === curr.code
                          ? 'bg-accent-soft text-accent-ink'
                          : 'text-ink-2 hover:bg-bg-sunk hover:text-ink'
                      }`}
                      role="option"
                      aria-selected={selectedCurrency === curr.code}
                    >
                      <span>{curr.name}</span>
                      <span className="font-semibold text-muted">{curr.symbol}</span>
                    </button>
                  ))}
                </MotionDiv>
              )}
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={toggleDarkMode}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line-strong bg-bg-elev/60 backdrop-blur-lg text-ink-2 transition-colors hover:bg-bg-sunk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={isDark ? 'sun' : 'moon'}
                initial={{ opacity: 0, scale: 0.7, rotate: -30 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.7, rotate: 30 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center justify-center"
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </motion.span>
            </AnimatePresence>
          </button>

          {user && isAuthenticated ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setIsProfileMenuOpen((value) => !value)}
                className="flex items-center gap-2 rounded-full border border-line-strong bg-bg-elev/60 backdrop-blur-lg px-1.5 py-1 text-left shadow-sm transition hover:border-accent hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-haspopup="menu"
                aria-expanded={isProfileMenuOpen}
              >
                <div className="relative h-8 w-8 overflow-hidden rounded-full ring-2 ring-accent">
                  {user?.picture && user.picture.length > 10 && !avatarFailed ? (
                    <img
                      src={user.picture}
                      alt={user?.name || 'Profile'}
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      className="h-full w-full object-cover"
                      width="32"
                      height="32"
                      loading="lazy"
                      decoding="async"
                      onError={() => setFailedAvatarUrl(user.picture)}
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
                      {avatarLetter}
                    </div>
                  )}
                </div>
                <ChevronDown className="h-4 w-4 text-muted" />
              </button>

              <AnimatePresence initial={false}>
                {isProfileMenuOpen ? (
                <MotionDiv
                  key="profile-menu"
                  role="menu"
                  aria-label="Profile menu"
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={dropdownTransition}
                  className="absolute right-0 mt-2 w-64 origin-top-right overflow-hidden rounded-2xl border border-line bg-bg-elev p-2 shadow-2xl"
                >
                  <div className="px-3 py-2">
                    <p className="truncate text-sm font-semibold text-ink">{user?.name || 'My account'}</p>
                    <p className="truncate text-xs text-muted">{user?.email || ''}</p>
                  </div>

                  <div className="my-2 border-t border-line" />

                  {isFounder ? (
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileMenuOpen(false)
                        navigate('/growth-hub')
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-accent transition hover:bg-accent-soft"
                      role="menuitem"
                    >
                      <Rocket className="h-4 w-4" />
                      Growth Hub
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => {
                      setIsProfileMenuOpen(false)
                      navigate('/dashboard')
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                    role="menuitem"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    My Dashboard
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsProfileMenuOpen(false)
                      navigate('/profile')
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                    role="menuitem"
                  >
                    <UserCircle2 className="h-4 w-4" />
                    Profile &amp; Settings
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsProfileMenuOpen(false)
                      navigate('/ai-tool-finder')
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                    role="menuitem"
                  >
                    <Compass className="h-4 w-4" />
                    AI Tool Finder
                  </button>

                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileMenuOpen(false)
                        navigate('/admin')
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-ink-2 transition hover:bg-bg-sunk"
                      role="menuitem"
                    >
                      <Shield className="h-4 w-4" />
                      Admin Panel
                    </button>
                  ) : null}

                  <div className="my-2 border-t border-line" />

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-danger transition hover:bg-danger-soft"
                    role="menuitem"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </MotionDiv>
                ) : null}
              </AnimatePresence>

              {showProfileTooltip && !isProfileMenuOpen && (
                <MotionDiv
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 5, scale: 0.95 }}
                  className="absolute right-0 top-full mt-3 z-50 w-80 rounded-2xl border border-accent bg-bg-elev p-4 shadow-2xl backdrop-blur-md"
                  style={{
                    borderColor: 'rgba(22, 131, 88, 0.45)',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {/* Arrow pointing up */}
                  <div 
                    className="absolute right-6 -top-1.5 h-3 w-3 rotate-45 border-l border-t border-accent bg-bg-elev" 
                    style={{ borderColor: 'rgba(22, 131, 88, 0.45) transparent transparent rgba(22, 131, 88, 0.45)' }} 
                  />

                  {/* Close button */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowProfileTooltip(false)
                      localStorage.setItem('ai-compass-profile-tooltip-dismissed', 'true')
                    }}
                    className="absolute right-3 top-3 text-muted hover:text-ink transition p-1 rounded-full hover:bg-bg-sunk"
                    aria-label="Close onboarding tooltip"
                  >
                    <X className="h-3 w-3" />
                  </button>

                  <div className="flex items-start gap-3 mt-1">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-ink border border-accent/20">
                      <Compass className="h-4.5 w-4.5" />
                    </div>
                    <div className="flex-1 min-w-0 pr-4">
                      <h4 className="text-xs font-bold text-ink tracking-tight">Meet your new AI Profile!</h4>
                      <p className="mt-1 text-xs leading-relaxed text-ink-2">
                        Customize recommendation goals, verify student status to claim discounts, and get a Gemini audit of your tool stack.
                      </p>
                      <div className="mt-3 flex items-center justify-between gap-2 pt-2 border-t border-line/45">
                        <Link
                          to="/profile"
                          onClick={() => {
                            setShowProfileTooltip(false)
                            localStorage.setItem('ai-compass-profile-tooltip-dismissed', 'true')
                          }}
                          className="text-xs font-bold text-accent hover:text-accent-ink hover:underline"
                        >
                          Customize Profile &rarr;
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            setShowProfileTooltip(false)
                            localStorage.setItem('ai-compass-profile-tooltip-dismissed', 'true')
                          }}
                          className="text-xs font-semibold text-muted hover:text-ink transition"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </MotionDiv>
              )}
            </div>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-ink-2">
                  Login
                </Button>
              </Link>

              <Link to="/register">
                <Button variant="primary" size="sm">
                  Register
                </Button>
              </Link>
            </>
          )}
        </nav>

        <button
          type="button"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          className="order-2 ml-auto inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line-strong bg-bg-elev text-ink-2 transition-colors hover:bg-bg-sunk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent sm:order-3 lg:hidden"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div id="mobile-menu" className="border-t border-line bg-bg-elev lg:hidden">
        <AnimatePresence initial={false} mode="wait">
          {isMobileMenuOpen && (
            <MotionDiv
              key="mobile-menu"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <nav aria-label="Mobile" className="flex flex-col px-4 py-3 gap-0.5">

                {/* Primary CTA — always visible at the top */}
                <Link
                  to="/ai-tool-finder"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-bold text-white shadow-sm mb-2 hover:opacity-90 transition"
                >
                  <Compass className="h-4 w-4" />
                  Find My Perfect AI Tool
                </Link>

                {/* ── DISCOVER ── */}
                <div className="px-3 pt-2 pb-0.5 text-xs font-bold tracking-wider text-muted uppercase">Discover</div>
                <Link
                  to="/tools"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Browse All Tools
                </Link>
                <Link
                  to="/collections"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Collections
                </Link>
                <Link
                  to="/compare"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Compare AI Tools
                </Link>
                <Link
                  to="/community"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Community &amp; Leaderboard
                </Link>
                <Link
                  to="/model-comparison"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  LLM Cost Calculator
                </Link>

                {/* ── STUDENT HUB ── */}
                <div className="border-t border-line/50 mt-2 mx-1" />
                <div className="px-3 pt-2 pb-0.5 text-xs font-bold tracking-wider text-muted uppercase">Student Hub</div>
                <Link
                  to="/best-ai-tools-for-students"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Best AI Tools for Students
                </Link>
                <Link
                  to="/best-free-ai-tools"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Best Free AI Tools
                </Link>
                <Link
                  to="/student-discounts"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Student Discounts
                </Link>
                <Link
                  to="/syllabus-parser"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Syllabus Parser
                </Link>
                <Link
                  to="/stacks"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Community Stacks
                </Link>
                <Link
                  to="/best-ai-tools-for-teachers"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  AI Tools for Teachers
                </Link>
                <Link
                  to="/best-coding-tools-for-students"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Best Coding Tools
                </Link>

                {/* ── HOW-TO GUIDES ── */}
                <div className="border-t border-line/50 mt-2 mx-1" />
                <div className="px-3 pt-2 pb-0.5 text-xs font-bold tracking-wider text-muted uppercase">How-To Guides</div>
                <Link
                  to="/guides/github-student-pack"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Claim GitHub Student Pack
                </Link>
                <Link
                  to="/guides/notion-student-premium"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Claim Notion Education Plan
                </Link>
                <Link
                  to="/guides/jetbrains-student-license"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-2 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  Claim JetBrains License
                </Link>

                {isAdmin ? (
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-muted hover:bg-bg-sunk"
                  >
                    Admin
                  </Link>
                ) : null}

                <div className="my-2 border-t border-line" />

                <button
                  type="button"
                  onClick={toggleDarkMode}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {isDark ? 'Light mode' : 'Dark mode'}
                </button>

                <div className="my-2 border-t border-line" />

                {/* Currency Selector (Mobile) */}
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm font-medium text-ink-2">Currency</span>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => {
                      setSelectedCurrency(e.target.value)
                      setIsMobileMenuOpen(false)
                    }}
                    className="rounded-xl border border-line bg-bg-sunk px-3 py-2 text-xs font-semibold text-ink-2 focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
                  >
                    {currencies.map((curr) => (
                      <option key={curr.code} value={curr.code}>
                        {curr.code} ({curr.symbol})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="my-2 border-t border-line" />

                {user && isAuthenticated ? (
                  <>
                    <div className="px-3 py-2">
                      <p className="truncate text-sm font-semibold text-ink">{user?.name || 'User'}</p>
                      <p className="truncate text-xs text-muted">{user?.email}</p>
                    </div>
                    {isFounder ? (
                      <Link
                        to="/growth-hub"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-accent hover:bg-accent-soft"
                      >
                        <Rocket className="h-4 w-4" />
                        Growth Hub
                      </Link>
                    ) : null}
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      My Dashboard
                    </Link>
                    <Link
                      to="/profile"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                    >
                      <UserCircle2 className="h-4 w-4" />
                      Profile &amp; Settings
                    </Link>
                    {isAdmin ? (
                      <Link
                        to="/admin"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                      >
                        <Shield className="h-4 w-4" />
                        Admin Panel
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        handleLogout()
                        setIsMobileMenuOpen(false)
                      }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-danger hover:bg-danger-soft"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg-sunk"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink hover:bg-bg-sunk"
                    >
                      Register
                    </Link>
                  </>
                )}
              </nav>
            </MotionDiv>
          )}
        </AnimatePresence>
      </div>

    </header>
  )
}

export default Navbar
